import { startSpan } from '@sentry/browser'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { Dayjs } from 'dayjs'
import stringify from 'fast-safe-stringify'

import { getOnlyDate } from '~/astra'
import optimusClient, { OptimusSchemas } from '~/clients/optimus-client'
import { mapDipsScheduledSurgeriesToDTO } from '~/hooks/tanstack-query/optimus/available-times/utils'
import { useFlag } from '~/hooks/useFlag'
import { cachedQueriesKeys } from '~/hooks/useWebSocketUpdates/handleWebSocketUpdates'
import { type AgeGroup, type ScheduledSurgery, type SchedulingSurgery, useAllAgeGroups, useOptimisticScheduledSurgeriesByDateRange } from '~/storm'
import { getMainSurgeonAndAssistants } from '~/utils/dips'

import { handleApiRequest } from './shared/apiWrapper'

type SurgeonWaitingListPredictionRequest = { startDate: Dayjs; endDate: Dayjs; surgeonNames: string[] }

const getAndMapWaitingListBySurgeonName = async (
    request: SurgeonWaitingListPredictionRequest,
    surgeries: (ScheduledSurgery | SchedulingSurgery)[],
    ageGroups: AgeGroup[],
    use_surgery_duration_model: boolean,
    shadow_run_surgery_duration_model: boolean,
    signal?: AbortSignal
) =>
    startSpan({ name: 'getAndMapWaitingListBySurgeonName' }, async span => {
        const dateParams = {
            end_date: getOnlyDate(request.endDate),
            start_date: getOnlyDate(request.startDate),
        }
        span.setAttribute(
            'request.params',
            stringify({
                ...dateParams,
                numberOfSurgeries: surgeries.length,
            })
        )
        const result = await handleApiRequest(() =>
            optimusClient.staffingPlan.getSurgeonWaitingListPrediction(
                {
                    ...dateParams,
                    surgeons: request.surgeonNames,
                    surgeries: mapDipsScheduledSurgeriesToDTO(surgeries, ageGroups, use_surgery_duration_model),
                    use_surgery_duration_model,
                    shadow_run_surgery_duration_model,
                },
                { signal }
            )
        )
        if (!result) {
            return undefined
        }
        const waitinglistBySurgeonName = result.surgeon_waiting_lists.reduce(
            (acc, curr) => {
                acc[curr.surgeon_short_name] = curr
                return acc
            },
            {} as Record<string, OptimusSchemas['SurgeonWaitingListResult']>
        )
        return {
            ...result,
            waitinglistBySurgeonName,
        }
    })

const waitingListPredictionQueryOptions = (
    request: SurgeonWaitingListPredictionRequest,
    surgeries: (ScheduledSurgery | SchedulingSurgery)[],
    ageGroups: AgeGroup[],
    use_surgery_duration_model: boolean,
    shadow_run_surgery_duration_model: boolean
) =>
    queryOptions({
        queryKey: [
            cachedQueriesKeys.surgeonWaitingListPrediction,
            {
                startDate: getOnlyDate(request.startDate),
                endDate: getOnlyDate(request.endDate),
                surgeonNames: request.surgeonNames.sort(),
                use_surgery_duration_model,
                shadow_run_surgery_duration_model,
            },
        ],
        queryFn: ({ signal }) =>
            getAndMapWaitingListBySurgeonName(request, surgeries, ageGroups, use_surgery_duration_model, shadow_run_surgery_duration_model, signal),
        retry: 1,
        staleTime: 60 * 1000,
    })

export type WaitingListPredictionQueryOptions = Omit<ReturnType<typeof waitingListPredictionQueryOptions>, 'queryKey' | 'queryFn'>

export const useSurgeonWaitingListPredictionQuery = (request: SurgeonWaitingListPredictionRequest, options?: WaitingListPredictionQueryOptions) => {
    const use_surgery_duration_model = useFlag('surgery_duration_model__enabled')
    const shadow_run_surgery_duration_model = useFlag('surgery_duration_model__shadow_run__enabled')
    const ageGroups = useAllAgeGroups()
    const resolvedEnabled = request.surgeonNames.length > 0 && options?.enabled

    // Storm hook: shallow-equality re-render guard means we only re-filter when
    // scheduled/un-scheduled slices actually change. The filtered closure is
    // captured by `queryFn` below so the queryFn always sees the latest
    // surgeries without an intermediate `useStore.getState()` snapshot.
    const surgeriesInRange = useOptimisticScheduledSurgeriesByDateRange(request.startDate, request.endDate)
    const surgeriesForSurgeons = surgeriesInRange.filter(surgery =>
        getMainSurgeonAndAssistants(surgery.surgeryResources).some(slot => !!slot.shortName && request.surgeonNames.includes(slot.shortName))
    )

    const query = useQuery({
        ...waitingListPredictionQueryOptions(request, surgeriesForSurgeons, ageGroups, use_surgery_duration_model, shadow_run_surgery_duration_model),
        ...options,
        enabled: resolvedEnabled,
    })

    return query
}
